/**
 * App 基本信息编辑表单
 */

import { useState, useRef } from 'react'
import { Loader2, Building2, Upload, ImageIcon, Save } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import { useToast } from '@/shared/hooks/useToast'
import { AppApiService, uploadIconToS3 } from '../services/app-api'
import type { App, UpdateAppRequest, AppPricing } from '../types/app'

interface AppEditFormProps {
  app: App
  onUpdate: (app: App) => void
}

export default function AppEditForm({ app, onUpdate }: AppEditFormProps) {
  const { showSuccess, showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 表单状态
  const [formData, setFormData] = useState({
    app_name: app.app_name,
    company: app.company || '',
    description: app.description || '',
    sms_sign_name: app.sms_sign_name,
    sms_template_verification: app.sms_template_verification,
    brand_color: app.brand_color || '#6366f1',
    trial_type: app.trial_type,
    trial_count: app.trial_type === 'count' ? (app.trial_config as any).default_count : 10,
    trial_duration:
      app.trial_type === 'duration' ? (app.trial_config as any).default_duration_seconds / 60 : 30,
    // 套餐价格
    tier1_monthly: app.pricing.tier1.monthly / 100,
    tier1_yearly: app.pricing.tier1.yearly / 100,
    tier2_monthly: app.pricing.tier2.monthly / 100,
    tier2_yearly: app.pricing.tier2.yearly / 100,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [iconPreview, setIconPreview] = useState<string | null>(app.icon_url)

  // 检查是否有修改
  const hasChanges = () => {
    return (
      formData.app_name !== app.app_name ||
      formData.company !== (app.company || '') ||
      formData.description !== (app.description || '') ||
      formData.sms_sign_name !== app.sms_sign_name ||
      formData.sms_template_verification !== app.sms_template_verification ||
      formData.brand_color !== (app.brand_color || '#6366f1') ||
      formData.trial_type !== app.trial_type ||
      formData.tier1_monthly !== app.pricing.tier1.monthly / 100 ||
      formData.tier1_yearly !== app.pricing.tier1.yearly / 100 ||
      formData.tier2_monthly !== app.pricing.tier2.monthly / 100 ||
      formData.tier2_yearly !== app.pricing.tier2.yearly / 100
    )
  }

  // 保存修改
  const handleSave = async () => {
    if (!formData.app_name.trim()) {
      showError('App 名称不能为空')
      return
    }

    setIsSaving(true)
    try {
      const pricing: AppPricing = {
        free: { name: '免费版', monthly: 0, yearly: 0 },
        tier1: {
          name: app.pricing.tier1.name,
          monthly: Math.round(formData.tier1_monthly * 100),
          yearly: Math.round(formData.tier1_yearly * 100),
        },
        tier2: {
          name: app.pricing.tier2.name,
          monthly: Math.round(formData.tier2_monthly * 100),
          yearly: Math.round(formData.tier2_yearly * 100),
        },
      }

      const updateData: UpdateAppRequest = {
        app_name: formData.app_name.trim(),
        company: formData.company.trim() || undefined,
        description: formData.description.trim() || undefined,
        sms_sign_name: formData.sms_sign_name.trim(),
        sms_template_verification: formData.sms_template_verification.trim(),
        brand_color: formData.brand_color,
        pricing,
        trial_type: formData.trial_type,
        trial_config:
          formData.trial_type === 'count'
            ? { default_count: formData.trial_count }
            : { default_duration_seconds: formData.trial_duration * 60 },
      }

      const response = await AppApiService.updateApp(app.app_id, updateData)
      if (response.success && response.data) {
        showSuccess('App 信息已保存')
        onUpdate(response.data)
      }
    } catch (error) {
      console.error('❌ [AppEditForm] 保存失败:', error)
      showError('保存失败，请稍后重试')
    } finally {
      setIsSaving(false)
    }
  }

  // 上传图标
  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 验证文件类型
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!validTypes.includes(file.type)) {
      showError('只支持 PNG、JPG、WEBP 格式的图片')
      return
    }

    // 验证文件大小（最大 1MB）
    if (file.size > 1024 * 1024) {
      showError('图片大小不能超过 1MB')
      return
    }

    setIsUploadingIcon(true)
    try {
      // 获取上传 URL
      const fileType = file.type.split('/')[1] as 'png' | 'jpg' | 'jpeg' | 'webp'
      const uploadResponse = await AppApiService.getAppIconUploadUrl(app.app_id, {
        file_type: fileType,
        file_size: file.size,
      })

      if (uploadResponse.success && uploadResponse.data) {
        // 上传到 S3
        await uploadIconToS3(uploadResponse.data.upload_url, file)

        // 更新 App icon_url
        const updateResponse = await AppApiService.updateApp(app.app_id, {
          icon_url: uploadResponse.data.icon_url,
        })

        if (updateResponse.success && updateResponse.data) {
          showSuccess('图标已上传')
          setIconPreview(uploadResponse.data.icon_url)
          onUpdate(updateResponse.data)
        }
      }
    } catch (error) {
      console.error('❌ [AppEditForm] 上传图标失败:', error)
      showError('上传图标失败，请稍后重试')
    } finally {
      setIsUploadingIcon(false)
      // 重置 input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* 基本信息卡片 */}
      <Card>
        <CardHeader>
          <CardTitle>基本信息</CardTitle>
          <CardDescription>App 的基本配置信息</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 图标上传 */}
          <div className="flex items-center gap-6">
            <div className="relative">
              {iconPreview ? (
                <img
                  src={iconPreview}
                  alt={app.app_name}
                  className="h-20 w-20 rounded-xl object-cover shadow-sm"
                />
              ) : (
                <div className="h-20 w-20 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm">
                  <ImageIcon className="h-10 w-10 text-primary" />
                </div>
              )}
              {isUploadingIcon && (
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleIconUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingIcon}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploadingIcon ? '上传中...' : '更换图标'}
              </Button>
              <p className="text-xs text-slate-500">支持 PNG、JPG、WEBP，最大 1MB</p>
            </div>
          </div>

          {/* App 名称 */}
          <div className="grid gap-2">
            <Label htmlFor="app_name">App 名称</Label>
            <Input
              id="app_name"
              value={formData.app_name}
              onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
            />
          </div>

          {/* 公司 */}
          <div className="grid gap-2">
            <Label htmlFor="company">公司/团队名称</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="company"
                className="pl-10"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
          </div>

          {/* 描述 */}
          <div className="grid gap-2">
            <Label htmlFor="description">描述</Label>
            <Textarea
              id="description"
              className="resize-none"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* 品牌颜色 */}
          <div className="grid gap-2">
            <Label htmlFor="brand_color">品牌颜色</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                id="brand_color"
                value={formData.brand_color}
                onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                className="h-10 w-10 rounded cursor-pointer border"
              />
              <Input
                value={formData.brand_color}
                onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                className="w-28"
                placeholder="#6366f1"
              />
              <span className="text-sm text-slate-500">用于邮件和通知的品牌色</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 短信配置卡片 */}
      <Card>
        <CardHeader>
          <CardTitle>短信配置</CardTitle>
          <CardDescription>用于发送验证码等短信通知</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="sms_sign_name">短信签名</Label>
            <Input
              id="sms_sign_name"
              value={formData.sms_sign_name}
              onChange={(e) => setFormData({ ...formData, sms_sign_name: e.target.value })}
            />
            <p className="text-xs text-slate-500">短信签名需要在短信平台提前注册</p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="sms_template_verification">验证码模板 ID</Label>
            <Input
              id="sms_template_verification"
              value={formData.sms_template_verification}
              onChange={(e) =>
                setFormData({ ...formData, sms_template_verification: e.target.value })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* 套餐配置卡片 */}
      <Card>
        <CardHeader>
          <CardTitle>套餐价格</CardTitle>
          <CardDescription>配置各套餐的月付和年付价格（单位：元）</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 基础版 */}
          <div className="space-y-2">
            <Label>基础版（Tier 1）</Label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 w-12">月付</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tier1_monthly}
                  onChange={(e) =>
                    setFormData({ ...formData, tier1_monthly: parseFloat(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">元</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 w-12">年付</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tier1_yearly}
                  onChange={(e) =>
                    setFormData({ ...formData, tier1_yearly: parseFloat(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">元</span>
              </div>
            </div>
          </div>

          {/* 高级版 */}
          <div className="space-y-2">
            <Label>高级版（Tier 2）</Label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 w-12">月付</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tier2_monthly}
                  onChange={(e) =>
                    setFormData({ ...formData, tier2_monthly: parseFloat(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">元</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 w-12">年付</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tier2_yearly}
                  onChange={(e) =>
                    setFormData({ ...formData, tier2_yearly: parseFloat(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">元</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 试用配置卡片 */}
      <Card>
        <CardHeader>
          <CardTitle>试用配置</CardTitle>
          <CardDescription>配置用户试用的默认规则</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label>试用计算方式</Label>
            <Select
              value={formData.trial_type}
              onValueChange={(value: 'count' | 'duration') =>
                setFormData({ ...formData, trial_type: value })
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="count">按次数</SelectItem>
                <SelectItem value="duration">按时长</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.trial_type === 'count' ? (
            <div className="grid gap-2">
              <Label>默认试用次数</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={formData.trial_count}
                  onChange={(e) =>
                    setFormData({ ...formData, trial_count: parseInt(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">次</span>
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>默认试用时长</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={formData.trial_duration}
                  onChange={(e) =>
                    setFormData({ ...formData, trial_duration: parseInt(e.target.value) || 0 })
                  }
                  className="w-28"
                />
                <span className="text-sm text-slate-500">分钟</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 保存按钮 */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving || !hasChanges()} className="gap-2">
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              保存中...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              保存修改
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
